<script>
  import { Bell, BellOff, LoaderCircle, LogOut, ShieldAlert, Trash2 } from 'lucide-svelte';

  export let isGroup = false;
  export let isCurrentUserOwner = false;
  export let leaveLabel = 'Thoát hội thoại';
  export let leaveError = '';
  export let actionError = '';
  export let notificationsMuted = false;
  export let notificationsMuteMode = 'off';
  export let notificationsMutedUntil = null;
  export let muting = false;
  export let leaving = false;
  export let dissolving = false;
  export let clearing = false;
  export let onMutePreset = () => {};
  export let onUnmute = () => {};
  export let onLeave = () => {};
  export let onDissolve = () => {};
  export let onClearHistory = () => {};

  const presets = [
    { label: '1 giờ', mode: 'until', durationMinutes: 60 },
    { label: '8 giờ', mode: 'until', durationMinutes: 480 },
    { label: '1 ngày', mode: 'until', durationMinutes: 1440 },
    { label: 'Đến khi bật lại', mode: 'forever', durationMinutes: null }
  ];

  $: muteStatus = notificationsMuted
    ? notificationsMuteMode === 'until' && notificationsMutedUntil
      ? `Đang tắt đến ${formatDateTime(notificationsMutedUntil)}`
      : 'Đang tắt cho đến khi bạn bật lại'
    : 'Đang bật bình thường';

  function formatDateTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'không xác định';

    return new Intl.DateTimeFormat('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    }).format(date);
  }
</script>

<div class="space-y-3 px-4 pb-5">
  <details class="rounded-lg border border-slate-200 bg-white" open={notificationsMuted}>
    <summary class="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-[12px] font-medium text-slate-900">
      <span class="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-50 text-slate-500">
        {#if notificationsMuted}
          <BellOff size={14} />
        {:else}
          <Bell size={14} />
        {/if}
      </span>
      <span class="min-w-0 flex-1">
        Tắt thông báo
        <span class="mt-0.5 block text-[11px] font-normal leading-5 text-slate-500">{muteStatus}</span>
      </span>
      <span class="text-[11px] font-normal text-slate-400">Tuỳ chọn</span>
    </summary>

    <div class="border-t border-slate-100 px-3 pb-3 pt-2">
      <div class="grid grid-cols-2 gap-2">
        {#each presets as preset}
          <button
            type="button"
            class="rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={muting || leaving || dissolving || clearing}
            onclick={() => onMutePreset(preset)}>
            {preset.label}
          </button>
        {/each}
      </div>

      {#if notificationsMuted}
        <button
          type="button"
          class="mt-2 w-full rounded-md bg-slate-900 px-3 py-2 text-[11px] font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={muting || leaving || dissolving || clearing}
          onclick={onUnmute}>
          {#if muting}
            <span class="inline-flex items-center gap-2">
              <LoaderCircle size={13} class="animate-spin" />
              Đang cập nhật
            </span>
          {:else}
            Bật lại thông báo
          {/if}
        </button>
      {/if}
    </div>
  </details>

  {#if leaveError}
    <p class="rounded-lg border border-red-100 bg-white px-3 py-2 text-[11px] text-red-600">{leaveError}</p>
  {/if}

  {#if actionError}
    <p class="rounded-lg border border-red-100 bg-white px-3 py-2 text-[11px] text-red-600">{actionError}</p>
  {/if}

  <div class="overflow-hidden rounded-lg border border-slate-200 bg-white">
    <button
      type="button"
      class="flex w-full items-center gap-3 px-3 py-3 text-left text-[13px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
      disabled={muting || leaving || dissolving || clearing}
      onclick={onClearHistory}>
      {#if clearing}
        <LoaderCircle size={15} class="animate-spin" />
      {:else}
        <Trash2 size={15} />
      {/if}
      Xoá lịch sử trò chuyện
    </button>

    {#if isGroup && isCurrentUserOwner}
      <button
        type="button"
        class="flex w-full items-center gap-3 border-t border-slate-100 px-3 py-3 text-left text-[13px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={muting || leaving || dissolving || clearing}
        onclick={onDissolve}>
        {#if dissolving}
          <LoaderCircle size={15} class="animate-spin" />
        {:else}
          <ShieldAlert size={15} />
        {/if}
        Giải tán nhóm
      </button>
    {/if}

    <button
      type="button"
      class="flex w-full items-center gap-3 border-t border-slate-100 px-3 py-3 text-left text-[13px] font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
      disabled={muting || leaving || dissolving || clearing}
      onclick={onLeave}>
      {#if leaving}
        <LoaderCircle size={15} class="animate-spin" />
      {:else}
        <LogOut size={15} />
      {/if}
      {leaveLabel}
    </button>
  </div>

  {#if isGroup && isCurrentUserOwner}
    <p class="px-1 text-[11px] leading-5 text-slate-500">
      Giải tán nhóm sẽ đóng hội thoại này với toàn bộ thành viên hiện tại.
    </p>
  {/if}
</div>
